"use client"

export const dynamic = 'force-dynamic'

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Search, Book, MessageCircle, FileText, Code, Zap, Globe, Key, DollarSign, Infinity } from "lucide-react"
import Link from "next/link"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"

export default function HelpPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            <Infinity className="w-3 h-3 mr-1" />
            Localization Without Limits, AI Without Markup
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Help Center
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Find answers about our unlimited localization platform, cost savings, and how to break free from enterprise pricing.
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input 
              placeholder="Search for help..." 
              className="pl-10 pr-4 py-2"
            />
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600 cursor-pointer">
            <CardHeader className="text-center">
              <Book className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <CardTitle>Getting Started</CardTitle>
              <CardDescription>Learn how to save 95% on localization costs</CardDescription>
            </CardHeader>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600 cursor-pointer">
            <CardHeader className="text-center">
              <Code className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <CardTitle>API Documentation</CardTitle>
              <CardDescription>Complete API reference with unlimited usage</CardDescription>
            </CardHeader>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600 cursor-pointer">
            <CardHeader className="text-center">
              <MessageCircle className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <CardTitle>Contact Support</CardTitle>
              <CardDescription>Get help from our team - no per-ticket fees</CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Popular Articles */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Popular Articles</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
              <CardHeader>
                <Badge variant="secondary" className="w-fit mb-2">Cost Savings</Badge>
                <CardTitle className="text-lg">How Much Can I Save vs Enterprise Platforms?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  Real cost comparisons showing how teams reduce localization costs by 90%+ when switching from enterprise platforms.
                </p>
                <span className="text-sm text-primary cursor-pointer hover:underline">
                  Read More →
                </span>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
              <CardHeader>
                <Badge variant="secondary" className="w-fit mb-2">Getting Started</Badge>
                <CardTitle className="text-lg">Setting Up Your OpenAI API Key</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  Learn how to securely configure your OpenAI API key to pay zero markup on AI translation costs.
                </p>
                <span className="text-sm text-primary cursor-pointer hover:underline">
                  Read More →
                </span>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600">
              <CardHeader>
                <Badge variant="secondary" className="w-fit mb-2">Integration</Badge>
                <CardTitle className="text-lg">Unlimited CI/CD Pipeline Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  Automate your translation workflow with unlimited API calls and no usage restrictions.
                </p>
                <span className="text-sm text-primary cursor-pointer hover:underline">
                  Read More →
                </span>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-orange-500/20 hover:border-orange-400 dark:hover:border-orange-600">
              <CardHeader>
                <Badge variant="secondary" className="w-fit mb-2">Best Practices</Badge>
                <CardTitle className="text-lg">Maximizing Translation Quality</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  Tips for getting enterprise-quality translations using your own AI models at actual cost.
                </p>
                <span className="text-sm text-primary cursor-pointer hover:underline">
                  Read More →
                </span>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
              <AccordionTrigger className="text-left px-4">
                How much will I actually pay compared to enterprise platforms?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  Most teams save 90-95% compared to enterprise platforms. Here's a real example for translating 100,000 words:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>Enterprise Platform:</strong> $2,000-5,000/month (platform fees + per-word pricing + per-user fees)</li>
                  <li>• <strong>Locsy Total Cost:</strong> $22-30/month ($20 platform fee + $2-10 actual OpenAI costs)</li>
                  <li>• <strong>Your Savings:</strong> $1,970-4,978/month or $23,640-59,736/year</li>
                <li>• <small className="text-muted-foreground">Cost estimates based on GPT-4.1-mini pricing for guidance only</small></li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
              <AccordionTrigger className="text-left px-4">
                What does "unlimited everything" really mean?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  Unlike enterprise platforms that charge per user, per language, per word, or per project, we have no limits:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>Unlimited languages:</strong> Translate to 100+ languages at no extra cost</li>
                  <li>• <strong>Unlimited team members:</strong> Invite unlimited translators, reviewers, stakeholders</li>
                  <li>• <strong>Unlimited projects:</strong> Organize unlimited applications and websites</li>
                  <li>• <strong>Unlimited files:</strong> Upload and process unlimited translation files</li>
                  <li>• <strong>Unlimited API calls:</strong> No usage caps or overage fees</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600">
              <AccordionTrigger className="text-left px-4">
                How does the "Bring Your Own Key" model work?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  You provide your own OpenAI API key, eliminating markup and giving you complete cost control:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>Zero markup:</strong> Pay OpenAI's actual cost (~$0.001-0.03 per 1K tokens)</li>
                  <li>• <strong>Complete transparency:</strong> Track every API call and cost in real-time</li>
                  <li>• <strong>Your control:</strong> Use your preferred OpenAI models and settings</li>
                  <li>• <strong>Maximum security:</strong> API key encrypted with AES-256, never shared</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-orange-500/20 hover:border-orange-400 dark:hover:border-orange-600">
              <AccordionTrigger className="text-left px-4">
                Why are enterprise platforms so expensive?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  Enterprise platforms charge massive markups because they can. Here's what you're really paying for:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>10-100x markup</strong> on actual AI translation costs</li>
                  <li>• <strong>Per-word pricing</strong> that scales with your success</li>
                  <li>• <strong>Per-user licensing</strong> that punishes team growth</li>
                  <li>• <strong>Feature restrictions</strong> locked behind expensive tiers</li>
                  <li>• <strong>Vendor lock-in</strong> with proprietary formats and APIs</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-red-500/20 hover:border-red-400 dark:hover:border-red-600">
              <AccordionTrigger className="text-left px-4">
                What if I'm currently using an enterprise platform?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  Switching is easy and immediately saves money. Here's what you need to know:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>Easy migration:</strong> Import your existing translation files and glossaries</li>
                  <li>• <strong>Better features:</strong> Get unlimited access to features previously locked behind expensive tiers</li>
                  <li>• <strong>No contracts:</strong> Cancel anytime, export all your data</li>
                  <li>• <strong>Immediate savings:</strong> Start saving 90%+ from month one</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-indigo-500/20 hover:border-indigo-400 dark:hover:border-indigo-600">
              <AccordionTrigger className="text-left px-4">
                Is my data secure and private?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  Yes, we provide enterprise-grade security for all users at no extra cost:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>AES-256 encryption</strong> for all data at rest and in transit</li>
                  <li>• <strong>Zero data retention:</strong> We don't store your translation content</li>
                  <li>• <strong>SOC 2 Type II compliance</strong> included for everyone</li>
                  <li>• <strong>API key isolation:</strong> Your keys are encrypted separately and never accessible</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-yellow-500/20 hover:border-yellow-400 dark:hover:border-yellow-600">
              <AccordionTrigger className="text-left px-4">
                How accurate are the AI translations?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  Using your own OpenAI key gives you access to the latest, most accurate models:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>Latest models:</strong> Access to GPT-4, GPT-4 Turbo, and future models as they're released</li>
                  <li>• <strong>Context awareness:</strong> AI understands UI context and brand tone</li>
                  <li>• <strong>Quality control:</strong> Unlimited reviewers for quality assurance</li>
                  <li>• <strong>Glossary support:</strong> Enforce consistent terminology across languages</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-teal-500/20 hover:border-teal-400 dark:hover:border-teal-600">
              <AccordionTrigger className="text-left px-4">
                Do you have rate limits or usage caps?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  No artificial limits from us - you're only bound by your own OpenAI account:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>No platform limits:</strong> Unlimited API calls, file uploads, downloads</li>
                  <li>• <strong>Your OpenAI limits:</strong> Bound only by your OpenAI account limits (which you control)</li>
                  <li>• <strong>Scale freely:</strong> Process millions of words without platform restrictions</li>
                  <li>• <strong>Burst capacity:</strong> Handle traffic spikes without throttling</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-9" className="border rounded-lg hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-pink-500/20 hover:border-pink-400 dark:hover:border-pink-600">
              <AccordionTrigger className="text-left px-4">
                Can I use this for commercial projects?
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <p className="text-muted-foreground mb-2">
                  Absolutely! Our platform is designed for commercial use at any scale:
                </p>
                <ul className="text-muted-foreground text-sm space-y-1 ml-4">
                  <li>• <strong>Commercial license:</strong> Use for any commercial project, no restrictions</li>
                  <li>• <strong>Enterprise features:</strong> All enterprise features included for everyone</li>
                  <li>• <strong>SLA guarantees:</strong> 99.9% uptime guarantee</li>
                  <li>• <strong>Business support:</strong> Priority support for business users</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Categories */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600 cursor-pointer">
              <CardHeader className="text-center py-6">
                <DollarSign className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <CardTitle className="text-sm">Cost Savings</CardTitle>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600 cursor-pointer">
              <CardHeader className="text-center py-6">
                <Key className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <CardTitle className="text-sm">API Keys</CardTitle>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600 cursor-pointer">
              <CardHeader className="text-center py-6">
                <Infinity className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <CardTitle className="text-sm">Unlimited Features</CardTitle>
              </CardHeader>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-orange-500/20 hover:border-orange-400 dark:hover:border-orange-600 cursor-pointer">
              <CardHeader className="text-center py-6">
                <FileText className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                <CardTitle className="text-sm">Migration Guide</CardTitle>
              </CardHeader>
            </Card>
          </div>
        </div>

        {/* Contact Support */}
        <Card className="max-w-2xl mx-auto hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
          <CardHeader className="text-center">
            <MessageCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <CardTitle>Still Need Help?</CardTitle>
            <CardDescription>
              Can't find what you're looking for? Get help calculating your savings or planning your migration.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Link href="/contact" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2">
                <MessageCircle className="mr-2 h-4 w-4" />
                Contact Support
              </Link>
              <a href="mailto:support@locsy.ai" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2">
                <FileText className="mr-2 h-4 w-4" />
                Email Us
              </a>
            </div>
            <div className="text-center text-sm text-muted-foreground">
              Average response time: 2-4 hours • No per-ticket fees
            </div>
          </CardContent>
        </Card>
      </section>

      <Footer />
    </div>
  )
} 